<template>
  <section class="documents-grid" role="region" aria-labelledby="documents-title">
    <div class="documents-grid__header">
      <div>
        <h2 id="documents-title">Documentos</h2>
        <p class="muted">{{ subtitle }}</p>
      </div>
      <div class="documents-grid__actions">
        <UiButton variant="ghost" size="sm" @click="$emit('create')">+ Nuevo documento</UiButton>
      </div>
    </div>

    <div class="documents-grid__cards">
      <template v-if="loading">
        <div v-for="n in 4" :key="`doc-skel-${n}`" class="doc-card">
          <div class="skeleton skeleton-line" style="width: 60%"></div>
          <div class="skeleton skeleton-line" style="width: 40%"></div>
          <div class="skeleton skeleton-block" style="height: 120px"></div>
        </div>
      </template>

      <template v-else>
        <article
          v-for="doc in documents"
          :key="doc.id"
          class="doc-card"
          tabindex="0"
          role="button"
          @click="$emit('open', doc)"
          @keydown.enter.prevent="$emit('open', doc)"
        >
          <header class="doc-card__header">
            <div class="doc-card__title">
              <span class="doc-card__icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" role="img" focusable="false">
                  <path
                    d="M14 2H7a3 3 0 00-3 3v14a3 3 0 003 3h10a3 3 0 003-3V8l-6-6zm0 2.5L19.5 10H15a1 1 0 01-1-1V4.5zM7 6h5v2H7V6zm0 4h10v2H7v-2zm0 4h10v2H7v-2z"
                    fill="currentColor"
                  />
                </svg>
              </span>
              <div>
                <h3>{{ doc.name }}</h3>
                <p class="muted">{{ doc.description || "Sin descripción" }}</p>
              </div>
            </div>
            <UiBadge :tone="doc.statusTone || 'neutral'">{{ doc.statusLabel }}</UiBadge>
          </header>

          <div class="doc-card__meta">
            <div>
              <span>Subida</span>
              <strong>{{ doc.uploadedAtLabel }}</strong>
            </div>
            <div>
              <span>Caduca</span>
              <strong>{{ doc.expiresAtLabel }}</strong>
            </div>
          </div>

          <div class="doc-card__preview">
            <img
              v-if="doc.preview"
              :src="doc.preview"
              :alt="doc.name"
              @click.stop="$emit('preview', { images: doc.imageFiles, index: 0, title: doc.name })"
            />
            <div v-else class="doc-card__fallback">
              <span>{{ doc.fileCount }} archivos</span>
            </div>
          </div>
        </article>

        <div v-if="!documents.length" class="empty-card">
          <p>{{ hasSearch ? "Sin resultados." : "No hay documentos todavía." }}</p>
          <UiButton v-if="!hasSearch" variant="ghost" size="sm" @click="$emit('create')">
            Crear el primero
          </UiButton>
        </div>
      </template>
    </div>

    <div v-if="error" class="state error">{{ error }}</div>
  </section>
</template>

<script>
import UiButton from "../ui/UiButton.vue";
import UiBadge from "../ui/UiBadge.vue";

export default {
  name: "DocumentsGrid",
  components: { UiButton, UiBadge },
  props: {
    documents: { type: Array, default: () => [] },
    subtitle: { type: String, default: "" },
    loading: { type: Boolean, default: false },
    error: { type: String, default: "" },
    hasSearch: { type: Boolean, default: false },
  },
};
</script>

<style scoped>
.documents-grid {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.documents-grid__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
  width: 100%;
}

.documents-grid__header h2 {
  margin: 0 0 6px;
}

.documents-grid__header p {
  margin: 0;
}

.documents-grid__cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 16px;
  width: 100%;
}

.doc-card {
  background: var(--color-card);
  border: 1px solid var(--border);
  border-radius: 18px;
  padding: 16px;
  display: grid;
  gap: 12px;
  cursor: pointer;
  transition: transform 0.2s ease, border-color 0.2s ease;
}

.doc-card:focus-visible {
  outline: 2px solid rgba(56, 189, 248, 0.7);
  outline-offset: 2px;
}

.doc-card:hover {
  transform: translateY(-2px);
  border-color: rgba(56, 189, 248, 0.4);
}

.doc-card__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}

.doc-card__title {
  display: flex;
  gap: 10px;
}

.doc-card__title h3 {
  margin: 0;
  font-size: 16px;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.doc-card__title p {
  margin: 4px 0 0;
  font-size: 12px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.doc-card__icon {
  width: 38px;
  height: 38px;
  border-radius: 12px;
  background: rgba(56, 189, 248, 0.16);
  color: var(--color-accent);
  display: grid;
  place-items: center;
  flex: 0 0 auto;
}

.doc-card__icon svg {
  width: 18px;
  height: 18px;
}

.doc-card__meta {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  font-size: 12px;
}

.doc-card__meta span {
  color: var(--muted);
}

.doc-card__meta strong {
  display: block;
  font-weight: 600;
  margin-top: 4px;
}

.doc-card__preview {
  border-radius: 14px;
  border: 1px solid rgba(148, 163, 184, 0.2);
  background: rgba(15, 23, 42, 0.5);
  overflow: hidden;
  aspect-ratio: 16 / 9;
  min-height: 140px;
  display: grid;
  place-items: center;
}

.doc-card__preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.doc-card__fallback {
  color: var(--muted);
  font-size: 12px;
}

</style>
